import {
  HOME_STAGE_ORDER,
  type CatalogItemDto,
  type DecorationAnchor,
  type DecorationAnchorType,
  type HomeStageId,
  type HomeStageOfferDto,
  type HomeStateResponse,
  type InventoryItem,
  type MyHomeResponse,
  type Offer,
  type PlacedItem,
  type WalletsResponse,
  type WorldManifest,
} from "@townsy/contracts";
import { anchorAccepts, getCatalogItem, getInterior, getPlot } from "@townsy/world";
import { HOME_STAGE_LABELS } from "../lib/format";
import { formatCoins } from "../lib/wallet";

/**
 * Pure rules for the homes UI (ADR-0005, docs/01 section 6 "Home management"). The browser never
 * decides that an upgrade, purchase or placement happens: it explains why a button is off, which
 * spots an item fits, and what the server answered. Every rule here is unit-tested.
 */

export type MyHome = NonNullable<MyHomeResponse["home"]>;
export type DecorScope = "yard" | "interior";

export function stageLabel(stage: string | null | undefined): string {
  if (!stage) return "—";
  return HOME_STAGE_LABELS[stage] ?? stage;
}

export function stageRank(stage: HomeStageId): number {
  return HOME_STAGE_ORDER.indexOf(stage);
}

// ---------------------------------------------------------------- availability

export type HomeBlockCode =
  | "SIGN_IN_REQUIRED"
  | "EMAIL_NOT_VERIFIED"
  | "FEATURE_DISABLED"
  | "HOME_LOADING"
  | "HOME_UNAVAILABLE"
  | "HOME_REQUIRED"
  | "TOP_STAGE"
  | "NOT_SOLD_FOR_COINS"
  | "WALLET_LOADING"
  | "WALLET_UNAVAILABLE"
  | "INSUFFICIENT_COINS";

export interface Availability {
  enabled: boolean;
  code: HomeBlockCode | null;
  /** Exact explanation shown next to the disabled button (null when enabled). */
  reason: string | null;
  /** Coins still missing when the balance is too low (0 otherwise). */
  shortfall: number;
}

/** Who is looking (from bootstrap): guests never buy; residents need a verified email. */
export interface Viewer {
  isGuest: boolean;
  emailVerified: boolean;
  /** `features.residentialProgressionEnabled` from bootstrap. */
  featureEnabled: boolean;
}

export type LoadState = "loading" | "ready" | "error" | "none";

export interface HomeInputs {
  viewer: Viewer;
  /** State of `GET /me/home` ("none" for guests). */
  homeState: LoadState;
  home: MyHome | null;
  wallets: Pick<WalletsResponse, "coins"> | null;
  walletState: LoadState;
}

const ENABLED: Availability = { enabled: true, code: null, reason: null, shortfall: 0 };
const off = (code: HomeBlockCode, reason: string, shortfall = 0): Availability => ({ enabled: false, code, reason, shortfall });

export const REAL_ESTATE_HINT = "Townsy Real Estate, 1 Plaza Way";

function gate(inputs: HomeInputs, verb: "buy items" | "upgrade a home"): Availability | null {
  const { viewer, homeState, home } = inputs;
  if (viewer.isGuest) return off("SIGN_IN_REQUIRED", `Sign in to ${verb}. Guests can look around but cannot own homes.`);
  if (!viewer.emailVerified) return off("EMAIL_NOT_VERIFIED", `Verify your email address to ${verb}.`);
  if (!viewer.featureEnabled) return off("FEATURE_DISABLED", "Home upgrades and items are switched off in this environment.");
  if (homeState === "loading") return off("HOME_LOADING", "Checking your home…");
  if (homeState === "error") return off("HOME_UNAVAILABLE", "Your home could not be loaded. Try again in a moment.");
  if (!home) return off("HOME_REQUIRED", `Buy a plot first at ${REAL_ESTATE_HINT}. Your home starts as a tent.`);
  return null;
}

function coinsCheck(price: number | null | undefined, inputs: HomeInputs): Availability {
  if (price === null || price === undefined) return off("NOT_SOLD_FOR_COINS", "Not offered for coins.");
  const { wallets, walletState } = inputs;
  if (walletState === "loading" || (walletState === "ready" && !wallets)) return off("WALLET_LOADING", "Checking your coin balance…");
  if (walletState !== "ready" || !wallets) return off("WALLET_UNAVAILABLE", "Your coin balance could not be loaded. Try again in a moment.");
  const have = wallets.coins.available;
  if (have < price) {
    const shortfall = price - have;
    return off("INSUFFICIENT_COINS", `Not enough coins: you need ${formatCoins(shortfall)} more (you have ${formatCoins(have)}).`, shortfall);
  }
  return ENABLED;
}

/**
 * The same account/home rules for the DOLLAR offers (card + Townsy Credit, M4): guests, unverified
 * email, switched-off homes and "no home yet" block both kinds of purchase. Null = no home rule blocks.
 */
export function homeDollarBlock(inputs: HomeInputs, what: "item" | "upgrade"): Availability | null {
  const blocked = gate(inputs, what === "item" ? "buy items" : "upgrade a home");
  if (blocked) return blocked;
  if (what === "upgrade" && !inputs.home?.nextUpgrade) return off("TOP_STAGE", "Your home is a castle: the top level. There is nothing left to upgrade.");
  return null;
}

/** Buy one catalog item with coins (it goes to storage; placing it is a separate, free step). */
export function itemBuyAvailability(inputs: HomeInputs & { offer: Pick<Offer, "coins"> }): Availability {
  return gate(inputs, "buy items") ?? coinsCheck(inputs.offer.coins, inputs);
}

/** The next home level, its exact coin price, and whether Upgrade is possible right now. */
export interface UpgradePlan extends Availability {
  next: HomeStageOfferDto | null;
  priceCoins: number | null;
  priceUsdCents: number | null;
}

export function upgradeAvailability(inputs: HomeInputs): UpgradePlan {
  const next = inputs.home?.nextUpgrade ?? null;
  const priceCoins = next?.offer?.coins ?? null;
  const priceUsdCents = next?.offer?.usdCents ?? null;
  const blocked = gate(inputs, "upgrade a home");
  if (blocked) return { ...blocked, next, priceCoins, priceUsdCents };
  if (!next) return { ...off("TOP_STAGE", "Your home is a castle: the top level. There is nothing left to upgrade."), next, priceCoins, priceUsdCents };
  return { ...coinsCheck(priceCoins, inputs), next, priceCoins, priceUsdCents };
}

// ---------------------------------------------------------------- stage ladder

export interface LadderStep {
  stage: HomeStageId;
  name: string;
  rank: number;
  status: "done" | "current" | "next" | "later";
  /** Price to upgrade INTO this level (null for the tent, which comes with the plot). */
  offer: Offer | null;
}

/** All seven levels in order, marking where this home is and which level comes next. */
export function stageLadder(stages: readonly HomeStageOfferDto[], current: HomeStageId | null): LadderStep[] {
  const byStage = new Map(stages.map((s) => [s.stage, s]));
  const currentRank = current ? stageRank(current) : -1;
  return HOME_STAGE_ORDER.map((stage, rank) => {
    const dto = byStage.get(stage);
    const status: LadderStep["status"] =
      current === null ? "later" : rank < currentRank ? "done" : rank === currentRank ? "current" : rank === currentRank + 1 ? "next" : "later";
    return { stage, name: dto?.name ?? stageLabel(stage), rank, status, offer: dto?.offer ?? null };
  });
}

// ---------------------------------------------------------------- anchors and fitting

export const ANCHOR_TYPE_LABELS: Record<DecorationAnchorType, string> = {
  yard_large: "Large back-yard spots",
  yard_medium: "Medium yard spot",
  yard_small: "Small garden spots",
  patio: "Patio",
  tree_spot: "Tree spots",
  fence_line: "Front fence line",
  floor_large: "Large floor spots",
  floor_small: "Small floor spots",
  table: "Dining spot",
  rug: "Rug spot",
  wall: "Wall spots",
};

/** What fits a spot type, for item cards ("Fits: large back-yard spots"). */
export const ANCHOR_TYPE_SHORT: Record<DecorationAnchorType, string> = {
  yard_large: "large back-yard spot",
  yard_medium: "medium yard spot",
  yard_small: "small garden spot",
  patio: "patio",
  tree_spot: "tree spot",
  fence_line: "front fence line",
  floor_large: "large floor spot",
  floor_small: "small floor spot",
  table: "dining spot",
  rug: "rug spot",
  wall: "wall",
};

const ANCHOR_NAMES: Record<string, string> = {
  back_large_a: "Back yard A",
  back_large_b: "Back yard B",
  side_medium: "Side yard",
  side_patio: "Side patio",
  front_small_a: "Front garden A",
  front_small_b: "Front garden B",
  side_small_a: "Side garden A",
  side_small_b: "Side garden B",
  back_small_a: "Back garden A",
  back_small_b: "Back garden B",
  tree_a: "Tree spot A",
  tree_b: "Tree spot B",
  tree_back: "Back tree spot",
  fence_front: "Front fence",
  sofa_spot: "Living area",
  bed_spot: "Bedroom corner",
  table_spot: "Dining spot",
  rug_center: "Centre of the room",
  corner_ne: "Back-right corner",
  corner_nw: "Left wall nook",
  corner_se: "Front-right corner",
  corner_sw: "Front-left corner",
  wall_north: "Back wall",
  wall_east: "Right wall",
  wall_west: "Left wall",
};

export function anchorLabel(anchorId: string): string {
  return ANCHOR_NAMES[anchorId] ?? anchorId.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function normalizeAngle(a: number): number {
  const t = Math.PI * 2;
  return ((a % t) + t) % t;
}

/** Rotation in words (world yaw: 0 = north, pi/2 = east). */
export function rotationLabel(rotation: number): string {
  const n = normalizeAngle(rotation);
  const near = (x: number) => Math.abs(n - x) < 1e-3 || Math.abs(n - x - Math.PI * 2) < 1e-3;
  if (near(0)) return "Facing north";
  if (near(Math.PI / 2)) return "Facing east";
  if (near(Math.PI)) return "Facing south";
  if (near((3 * Math.PI) / 2)) return "Facing west";
  return `Turned ${Math.round((n * 180) / Math.PI)}°`;
}

export function sameRotation(a: number, b: number): boolean {
  return Math.abs(normalizeAngle(a) - normalizeAngle(b)) < 1e-3;
}

/** The predefined spots of one home: yard anchors on the plot, or furniture anchors in its one room. */
export function anchorsForScope(manifest: WorldManifest, propertyId: string, scope: DecorScope): DecorationAnchor[] {
  const plot = getPlot(manifest, propertyId);
  if (!plot || plot.type !== "RESIDENTIAL") return [];
  if (scope === "yard") return plot.decorationAnchors;
  try {
    return getInterior(manifest, plot.interiorTemplate).decorationAnchors ?? [];
  } catch {
    return [];
  }
}

export function placedInScope(placed: readonly PlacedItem[], scope: DecorScope): PlacedItem[] {
  return placed.filter((p) => (p.scope ?? "yard") === scope);
}

export function occupiedAnchorIds(placed: readonly PlacedItem[], scope: DecorScope): Set<string> {
  return new Set(placedInScope(placed, scope).map((p) => p.anchorId));
}

export function freeAnchors(anchors: readonly DecorationAnchor[], placed: readonly PlacedItem[], scope: DecorScope): DecorationAnchor[] {
  const taken = occupiedAnchorIds(placed, scope);
  return anchors.filter((a) => !taken.has(a.id));
}

/** Rotations of this anchor that accept the item (same rule as the server: `anchorAccepts`). */
export function fittingRotations(anchor: DecorationAnchor, itemId: string): number[] {
  const def = getCatalogItem(itemId);
  if (!def) return [];
  return anchor.allowedRotations.filter((r) => anchorAccepts(anchor, def, r));
}

export function itemFitsAnchor(anchor: DecorationAnchor, itemId: string): boolean {
  return fittingRotations(anchor, itemId).length > 0;
}

/** Owned instances that are in storage (not placed anywhere). */
export function storedItems(inventory: readonly InventoryItem[]): InventoryItem[] {
  return inventory.filter((i) => i.placement === null);
}

/** Stored items that fit this spot with at least one allowed rotation. */
export function itemsFittingAnchor(anchor: DecorationAnchor, inventory: readonly InventoryItem[]): InventoryItem[] {
  return storedItems(inventory).filter((i) => itemFitsAnchor(anchor, i.itemId));
}

/** Free spots in a scope that accept a given item (for "where can this go?"). */
export function anchorsAcceptingItem(anchors: readonly DecorationAnchor[], itemId: string): DecorationAnchor[] {
  return anchors.filter((a) => itemFitsAnchor(a, itemId));
}

export interface AnchorGroup {
  type: DecorationAnchorType;
  label: string;
  anchors: Array<{ anchor: DecorationAnchor; occupiedBy: PlacedItem | null }>;
}

/** Spots grouped by type in a stable order, each marked free or occupied. */
export function groupAnchors(anchors: readonly DecorationAnchor[], placed: readonly PlacedItem[], scope: DecorScope): AnchorGroup[] {
  const inScope = placedInScope(placed, scope);
  const byAnchor = new Map(inScope.map((p) => [p.anchorId, p]));
  const order = Object.keys(ANCHOR_TYPE_LABELS) as DecorationAnchorType[];
  const groups: AnchorGroup[] = [];
  for (const type of order) {
    const list = anchors.filter((a) => a.type === type);
    if (list.length === 0) continue;
    groups.push({ type, label: ANCHOR_TYPE_LABELS[type], anchors: list.map((anchor) => ({ anchor, occupiedBy: byAnchor.get(anchor.id) ?? null })) });
  }
  return groups;
}

export function catalogItemName(itemId: string, catalog?: readonly Pick<CatalogItemDto, "itemId" | "name">[] | null): string {
  return catalog?.find((c) => c.itemId === itemId)?.name ?? getCatalogItem(itemId)?.name ?? itemId;
}

/** "You own 2 (1 in storage)" for a catalog card. */
export function ownedCount(inventory: readonly InventoryItem[] | null | undefined, itemId: string): { owned: number; stored: number } {
  const mine = (inventory ?? []).filter((i) => i.itemId === itemId);
  return { owned: mine.length, stored: mine.filter((i) => i.placement === null).length };
}

/**
 * Apply a mutation's fresh state to the cached home. A response older than what is cached (a slow
 * reply that raced a newer change) never rolls the layout back.
 */
export function mergeHomeState(home: MyHome, state: HomeStateResponse, stages?: readonly HomeStageOfferDto[] | null): MyHome {
  if (state.propertyId !== home.propertyId || state.layoutVersion < home.layoutVersion) return home;
  const upgraded = state.stage !== home.stage;
  return {
    ...home,
    stage: state.stage,
    layoutVersion: state.layoutVersion,
    access: state.access,
    look: state.look,
    placedItems: state.placedItems,
    // The next offer comes from the server; after an upgrade it is derived from the public catalog
    // until `GET /me/home` is refetched (callers always refetch after a change).
    nextUpgrade: upgraded ? nextStageOffer(stages ?? [], state.stage, home.nextUpgrade) : home.nextUpgrade,
  };
}

/** The catalog entry for the level after `stage` (null at the castle). */
export function nextStageOffer(
  stages: readonly HomeStageOfferDto[],
  stage: HomeStageId,
  fallback: HomeStageOfferDto | null = null,
): HomeStageOfferDto | null {
  const nextId = HOME_STAGE_ORDER[stageRank(stage) + 1];
  if (!nextId) return null;
  return stages.find((s) => s.stage === nextId) ?? (fallback?.stage === nextId ? fallback : null);
}
